<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';

const props = defineProps({
    testimonials: {
        type: Array,
        required: true
    },
    autoplayInterval: {
        type: Number,
        default: 5000 // 5 seconds between slides by default
    },
    autoplayDelay: {
        type: Number,
        default: 800 // Delay before resuming autoplay after mouse leaves
    }
});

const activeIndex = ref(0);
const sliderRef = ref(null);
const isAnimating = ref(false);
const isPaused = ref(false);
const autoplayTimer = ref(null);
const testimonialCount = computed(() => {
    return props.testimonials.length;
});

const slideItems = computed(() => {
    return props.testimonials.map((item, index) => {
        // Calculate position relative to active index
        const position = calculatePosition(index);
        return {
            ...item,
            index,
            position,
            classes: getClassForPosition(position)
        };
    });
});

function calculatePosition(index) {
    let position = index - activeIndex.value;
    
    // Handle wrap-around for infinite scrolling effect
    if (position > Math.floor(testimonialCount.value / 2)) {
        position -= testimonialCount.value;
    }
    if (position < -Math.floor(testimonialCount.value / 2)) {
        position += testimonialCount.value;
    }
    
    return position;
}

function getClassForPosition(position) {
    // Position-specific classes using Tailwind
    if (position === 0) {
        // Active/center slide - scale 110%
        return "z-40 bg-white opacity-100 scale-100";
    } else if (Math.abs(position) === 1) {
        // Adjacent slides - scale 105%
        return `z-30 bg-white ${position < 0 ? '-translate-x-96 sm:-translate-x-[36rem]' : 'translate-x-96 sm:translate-x-[36rem]'} scale-95`;
    } else if (Math.abs(position) === 2) {
        // Further slides - scale 100%
        return `z-20 bg-white ${position < 0 ? '-translate-x-[72rem]' : 'translate-x-[72rem]'} scale-90`;
    } else {
        // Hidden slides - scale 95%
        return `z-10 bg-white ${position < 0 ? '-translate-x-[108rem]' : 'translate-x-[108rem]'} scale-[.85]`;
    }
}

function prevSlide() {
    if (isAnimating.value) return;
    isAnimating.value = true;
    activeIndex.value = (activeIndex.value - 1 + testimonialCount.value) % testimonialCount.value;
    setTimeout(() => {
        isAnimating.value = false;
    }, 500); // Match this with the transition duration in the CSS
}

function nextSlide() {
    if (isAnimating.value) return;
    isAnimating.value = true;
    activeIndex.value = (activeIndex.value + 1) % testimonialCount.value;
    setTimeout(() => {
        isAnimating.value = false;
    }, 500); // Match this with the transition duration in the CSS
}

function goToSlide(index) {
    if (isAnimating.value || index === activeIndex.value) return;
    isAnimating.value = true;
    activeIndex.value = index;
    setTimeout(() => {
        isAnimating.value = false;
    }, 500);
}

let touchStartX = 0;
let touchEndX = 0;

function handleTouchStart(e) {
    touchStartX = e.changedTouches[0].screenX;
}

function handleTouchEnd(e) {
    touchEndX = e.changedTouches[0].screenX;
    handleSwipe();
}

function handleSwipe() {
    const threshold = 50; // Minimum distance required for a swipe
    if (touchEndX < touchStartX - threshold) {
        // Swipe left, go to next
        nextSlide();
    }
    if (touchEndX > touchStartX + threshold) {
        // Swipe right, go to previous
        prevSlide();
    }
}

// Add helper function to generate stars array for rating display
function getStarsArray(rating) {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    
    const stars = [];
    
    // Add full stars
    for (let i = 0; i < fullStars; i++) {
        stars.push('full');
    }
    
    // Add half star if needed
    if (hasHalfStar) {
        stars.push('half');
    }
    
    // Add empty stars to fill up to 5
    const emptyStars = 5 - stars.length;
    for (let i = 0; i < emptyStars; i++) {
        stars.push('empty');
    }
    
    return stars;
}

function startAutoplay() {
    if (autoplayTimer.value) {
        clearInterval(autoplayTimer.value);
    }
    
    autoplayTimer.value = setInterval(() => {
        if (!isPaused.value && !isAnimating.value) {
            nextSlide();
        }
    }, props.autoplayInterval);
}

function pauseAutoplay() {
    isPaused.value = true;
}

function resumeAutoplay() {
    // Use setTimeout to add a slight delay before resuming
    setTimeout(() => {
        isPaused.value = false;
    }, props.autoplayDelay);
}

// Initialize autoplay when component is mounted
onMounted(() => {
    startAutoplay();
});

// Clean up timer when component is unmounted
onBeforeUnmount(() => {
    if (autoplayTimer.value) {
        clearInterval(autoplayTimer.value);
    }
});
</script>

<template>
    <div class="relative w-full overflow-hidden px-4" @mouseenter="pauseAutoplay" @mouseleave="resumeAutoplay">
        <div ref="sliderRef" class="flex items-center justify-center h-72 md:h-96" @touchstart="handleTouchStart" @touchend="handleTouchEnd">
            <div v-for="slide in slideItems" :key="slide.index" :class="['absolute w-11/12 max-w-96 sm:max-w-xl transition-all duration-500 ease-in-out transform',
                'rounded-2xl shadow-lg shadow-deepblue/10 p-4 md:p-6 lg:p-8 flex flex-col justify-between', slide.classes, 
                { 'cursor-pointer hover:brightness-105': slide.position !== 0 } ]" @click="goToSlide(slide.index)">
                <div class="flex items-center justify-between w-full">
                    <div class="flex items-center gap-x-4">
                        <div class="flex-shrink-0">
                            <div v-if="slide.author.avatar" class="h-12 w-12 rounded-full overflow-hidden">
                                <img :src="slide.author.avatar" alt="" class="h-full w-full object-cover" />
                            </div>
                            <div v-else class="h-10 w-10 rounded-full bg-royalblue-500/20 flex items-center justify-center text-royalblue-500 font-semibold">
                                {{ slide.author.name.charAt(0) }}
                            </div>
                        </div>
                        <div>
                            <div class="font-semibold text-sm sm:text-base">{{ slide.author.name }}</div>
                            <div v-if="slide.author.title" class="text-sm text-gray-500">{{ slide.author.title }}</div>
                        </div>
                    </div>

                    <div class="flex items-center mt-2">
                        <!-- Dynamic star rating based on testimonial rating -->
                        <template v-for="(starType, index) in getStarsArray(slide.rating || 5)" :key="index">
                            <font-awesome-icon :icon="[ 'fas', starType === 'full' ? 'star' : starType === 'half' ? 'star-half-stroke' : 'star' ]"
                                :class="[ 'h-3.5 w-3.5 shrink-0', starType === 'empty' ? 'text-royalblue-500/20' : 'text-brightorange' ]" aria-hidden="true" />
                        </template>
                        <span class="sr-only">{{ slide.rating || 5 }} van de 5 sterren</span>
                    </div>
                </div>
                <p class="text-gray-900 mt-3 md:mt-6 text-sm sm:text-base md:text-lg line-clamp-5 h-[7.25em] md:h-[7.5em]">{{ `"${slide.body}"` }}</p>
                <p class="text-royalblue-500 mt-0 text-sm sm:text-base md:text-lg font-semibold pt-2 md:pt-4">{{ slide.author.date }}</p>
            </div>
        </div>
    </div>
    <!-- Pagination indicators -->
    <div class="flex justify-center gap-2">
        <button v-for="(_, index) in testimonials" :key="index" @click="goToSlide(index)" 
            class="w-4 h-2.5 rounded-full transition-all duration-300 focus:outline-none shadow-2xl shadow-deepblue"
            :class="index === activeIndex ? 'bg-brightorange w-8' : 'bg-gray-300 hover:bg-lessbrightorange'"></button>
    </div>
</template>
